import { useMemo } from "react";
import useSWR from "swr";
import type { Limrun } from "@limrun/api";
import { useAuth } from "@/auth/auth.ts";
import { orgRequestOptions } from "@/client/director/org.ts";

// A peer is the other half of an iOS simulator <-> Xcode instance pairing.
export type InstancePeer = { platform: "ios" | "xcode"; id: string };

type LimbuildSimulatorStatus = {
	attached?: boolean;
	simulator?: { iosInstanceId?: string };
};

async function attachedSimulatorId(apiUrl: string, token: string): Promise<string | undefined> {
	const url = new URL(`${apiUrl}/simulator`);
	url.searchParams.set("token", token);
	const response = await fetch(url.toString());
	if (!response.ok) throw new Error(`Could not read simulator attachment: HTTP ${response.status}`);
	const body = (await response.json()) as LimbuildSimulatorStatus;
	return body.attached ? body.simulator?.iosInstanceId : undefined;
}

// The director has no reverse attachment index. Check every page of live builders
// and match the daemon's simulator ID, never a display name or creation time.
export function useAttachedXcodeInstance(simulatorId: string | undefined) {
	const auth = useAuth();
	return useSWR(
		simulatorId ? ["onboardingBuilder", auth.organizationTid, simulatorId] : null,
		async () => {
			let page = await auth.regionClient.xcodeInstances.list(
				{ state: "ready,assigned" }, orgRequestOptions(auth.organizationTid),
			);
			const builders = [...page.getPaginatedItems()];
			while (page.hasNextPage()) {
				page = await page.getNextPage();
				builders.push(...page.getPaginatedItems());
			}
			builders.sort((a, b) => (b.metadata.createdAt ?? "").localeCompare(a.metadata.createdAt ?? ""));
			const attachments = await Promise.allSettled(builders.map(async (builder) => {
				if (!builder.status?.apiUrl || !builder.status.token) return undefined;
				return await attachedSimulatorId(builder.status.apiUrl, builder.status.token) === simulatorId
					? builder.metadata.id : undefined;
			}));
			const match = attachments.find((result) => result.status === "fulfilled" && result.value);
			if (match?.status === "fulfilled") return match.value;
			const failed = attachments.find((result) => result.status === "rejected");
			if (failed?.status === "rejected") throw failed.reason;
			return null;
		},
		{ refreshInterval: 5_000 },
	);
}

// Resolves which iOS simulators the listed Xcode instances are attached to.
// Attachments live only in limbuild, so every live Xcode instance is asked via
// GET /simulator. The map is keyed by instance id and holds peers for both
// directions.
export function useInstanceConnections(xcodeInstances: Limrun.XcodeInstance[]): Map<string, InstancePeer[]> {
	const targets = xcodeInstances.flatMap((instance) => {
		const { state, apiUrl, token } = instance.status ?? {};
		return (state === "ready" || state === "assigned") && apiUrl && token
			? [{ id: instance.metadata.id, apiUrl, token }]
			: [];
	});
	const targetKey = targets
		.map((target) => target.id)
		.sort()
		.join(",");

	const { data: attachments } = useSWR(
		targetKey ? ["xcodeSimulatorAttachments", targetKey] : null,
		async () => {
			const entries = await Promise.all(
				targets.map(async (target): Promise<[string, string | undefined]> => {
					return [target.id, await attachedSimulatorId(target.apiUrl, target.token).catch(() => undefined)];
				}),
			);
			return Object.fromEntries(entries) as Record<string, string | undefined>;
		},
		{ refreshInterval: 30_000, keepPreviousData: true },
	);

	return useMemo(() => {
		const peers = new Map<string, InstancePeer[]>();
		const link = (iosId: string, xcodeId: string) => {
			const add = (from: string, peer: InstancePeer) => {
				const existing = peers.get(from) ?? [];
				if (!existing.some((p) => p.id === peer.id)) {
					peers.set(from, [...existing, peer]);
				}
			};
			add(iosId, { platform: "xcode", id: xcodeId });
			add(xcodeId, { platform: "ios", id: iosId });
		};
		for (const [xcodeId, iosId] of Object.entries(attachments ?? {})) {
			if (iosId) link(iosId, xcodeId);
		}
		return peers;
	}, [attachments]);
}

// Current state of iOS instances the page has not listed itself, such as a
// peer reported by limbuild while only the Xcode tab is loaded. limbuild
// keeps reporting an attachment after the simulator terminates, so the state
// has to come from the director. Ids that fail to load stay absent.
export function useIosInstanceStates(ids: string[]): Map<string, string> {
	const auth = useAuth();
	const key = [...ids].sort().join(",");
	const { data } = useSWR(
		key ? ["iosInstanceStates", auth.organizationTid, key] : null,
		async () => {
			const entries = await Promise.all(
				key.split(",").map(async (id): Promise<[string, string | undefined]> => {
					try {
						const instance = await auth.regionClient.iosInstances.get(
							id,
							orgRequestOptions(auth.organizationTid),
						);
						return [id, instance.status?.state];
					} catch {
						return [id, undefined];
					}
				}),
			);
			return Object.fromEntries(entries) as Record<string, string | undefined>;
		},
		{ refreshInterval: 30_000, keepPreviousData: true },
	);
	return useMemo(() => {
		const states = new Map<string, string>();
		for (const [id, state] of Object.entries(data ?? {})) {
			if (state) states.set(id, state);
		}
		return states;
	}, [data]);
}
